"use client"

import { useState } from "react"
import { ApertureStyles, T, MAXW, SectionEyebrow } from "./_shared"

export default function ApertureFaq() {
  // FAQ entries answer the actual objections that block conversion —
  // accuracy (will it work?), data safety (can I trust it?), pricing
  // ("$29 vs $300 is suspicious"), integration ("can I plug it in?")
  // and refund (risk-reversal). Kept honest — no JSON/Parquet/SDK
  // claims that don't ship yet; only what /api/v1/clean actually does.
  const items = [
    { q: "How does the cleaner decide what to flag?",
      a: "Every row runs through the same checks: syntax, MX, catch-all domain behavior, duplicate, role-based, no-reply, disposable, risky TLD, and typo suggestions. The export includes the verdict and reason so you can review the decision instead of trusting a black-box score." },
    { q: "Why is the pricing simpler than credit-based validators?",
      a: "DataBridge is priced around monthly row allowance, not one-off credit math. The goal is predictable cleanup for teams that process lists every month and need the audit trail, mapper, compare tool, and API in one place." },
    { q: "What happens to my CSV after the run?",
      a: "Your source and cleaned files live in your account storage so you can download, map, compare, or delete them later. We don't sell uploaded lists or train models on your contacts. Delete a dataset from /dashboard/datasets when you no longer need it." },
    { q: "Can I plug it into my existing pipeline?",
      a: "Yes — paid plans get a Bearer-auth public API at /api/v1/clean. Send a CSV and get a cleaned CSV back with verdict + reason per row. Webhooks can notify your pipeline when dashboard jobs finish. Full reference at /docs/api." },
    { q: "What if my list is bigger than the plan limit?",
      a: "We never silently charge over. You'll see a paywall at 100% with the option to top up rows or upgrade. No surprise invoices, ever." },
    { q: "Do you have a refund policy?",
      a: "Cancel anytime from /dashboard/billing — your subscription stops at the end of the current period and you keep access until then. Email support@databridge.so within 7 days of a charge if something went wrong." },
  ]
  const [open, setOpen] = useState<number | null>(0)
  return (
    <section className="relative">
      <ApertureStyles />
      <div className={`${MAXW} mx-auto px-6 lg:px-10 py-12 sm:py-16 md:py-24`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <SectionEyebrow>Questions</SectionEyebrow>
            <h2
              className="mt-3 leading-[1.0] tracking-[-0.035em]"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.6rem)", fontWeight: 800 }}
            >
              The questions <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>before you sign up.</em>
            </h2>
            <p className="mt-4" style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.6 }}>
              Didn&rsquo;t find it? Email <a href="mailto:support@databridge.so" className="font-semibold" style={{ color: T.acc }}>support@databridge.so</a> — Damian reads every one.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div
              className="rounded-2xl"
              style={{
                background: T.surface,
                border: `1px solid ${T.hair}`,
                boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset`,
              }}
            >
              {items.map((it, i) => {
                const isOpen = open === i
                return (
                  <div key={it.q} style={{ borderTop: i > 0 ? `1px solid ${T.hair}` : undefined }}>
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left"
                    >
                      <span style={{ fontSize: 15, fontWeight: 600, color: T.ink, letterSpacing: "-0.01em" }}>
                        {it.q}
                      </span>
                      <span
                        className="flex-shrink-0 transition-transform"
                        style={{ transform: isOpen ? "rotate(45deg)" : "rotate(0)", color: isOpen ? T.acc : T.ink3 }}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </span>
                    </button>
                    <div
                      style={{
                        maxHeight: isOpen ? 240 : 0,
                        overflow: "hidden",
                        transition: "max-height 280ms ease",
                      }}
                    >
                      <p className="px-6 pb-6" style={{ fontSize: 14, color: T.ink2, lineHeight: 1.65, maxWidth: 640 }}>
                        {it.a}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
